// ReactivateAccountPage.jsx - Public page for reactivating a pending-deletion account
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authService } from "../../services/auth";
import logger from "../../utils/logger";

function ReactivateAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const hasCalled = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("missing_token");
      return;
    }

    // React StrictMode can mount the effect twice; the reactivation endpoint
    // consumes the token, so a duplicate call would return 400 and overwrite
    // the success state. Guard against double-invocation.
    if (hasCalled.current) return;
    hasCalled.current = true;

    const reactivate = async () => {
      try {
        await authService.reactivateAccount(token);
      } catch (err) {
        logger.error("Account reactivation failed:", err);
        setError(err.message || "reactivation_failed");
      } finally {
        setLoading(false);
      }
    };

    reactivate();
  }, [token]);

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    background: "var(--bg-primary, #fff)",
  };

  const cardStyle = {
    maxWidth: "420px",
    width: "100%",
    padding: "2rem",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    background: "var(--bg-primary, #fff)",
    textAlign: "center",
  };

  const headingStyle = {
    fontSize: "1.5rem",
    marginBottom: "1rem",
    color: "var(--text-primary, #1a1a1a)",
  };

  const bodyStyle = {
    color: "var(--text-secondary, #555)",
    marginBottom: "1.5rem",
    lineHeight: 1.5,
  };

  const primaryBtnStyle = {
    padding: "0.75rem 1.5rem",
    borderRadius: "12px",
    border: "none",
    background: "var(--primary-color, #2E7D32)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={bodyStyle}>Reactivating your account…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⏰</div>
          <h2 style={headingStyle}>Reactivation link invalid or expired</h2>
          <p style={bodyStyle}>
            This link is no longer valid. If your account has already been permanently deleted, you will need to create a new account.
          </p>
          <button type="button" onClick={() => navigate("/")} style={primaryBtnStyle}>
            Go home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✅</div>
        <h2 style={headingStyle}>Account reactivated</h2>
        <p style={bodyStyle}>
          Your account has been restored and is active again. You can now continue using Pista.
        </p>
        <button type="button" onClick={() => navigate("/")} style={primaryBtnStyle}>
          Continue
        </button>
      </div>
    </div>
  );
}

export default ReactivateAccountPage;
